import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Checkbox, Chip, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, Stack, TextField, Typography, } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useCreatePitch, useRetrieveVenue } from "../api/VenueApiProvider";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { FormActions } from "../utils/FormActions";
const SIZE_OPTIONS = [
    { value: "5", label: "5-a-side" },
    { value: "6", label: "6-a-side" },
    { value: "7", label: "7-a-side" },
    { value: "8", label: "8-a-side" },
    { value: "9", label: "9-a-side" },
    { value: "11", label: "11-a-side" },
];
const SURFACE_OPTIONS = [
    { value: "grass", label: "Grass" },
    { value: "artificial_turf", label: "Artificial Turf" },
    { value: "astroturf", label: "Astroturf" },
    { value: "indoor", label: "Indoor" },
    { value: "hybrid", label: "Hybrid" },
];
const CreatePitch = () => {
    const navigate = useNavigate();
    const { venueId } = useParams();
    const venueIdNum = venueId ? parseInt(venueId, 10) : undefined;
    const { control, handleSubmit, formState: { errors }, } = useForm({
        defaultValues: {
            name: "",
            description: "",
            sizes: [],
            surface: "artificial_turf",
            isIndoor: false,
        },
    });
    const { data: venue, isLoading: isLoadingVenue, error: venueError, } = useRetrieveVenue({
        id: venueIdNum,
        enabled: !!venueIdNum,
    });
    const createMutation = useCreatePitch({
        onSuccess: () => {
            navigate(`/venues/${venueIdNum}`);
        },
    });
    const onSubmit = (data) => {
        if (!venueIdNum)
            return;
        createMutation.mutate({
            venue: venueIdNum,
            ...data,
        });
    };
    const handleCancel = () => {
        navigate(`/venues/${venueIdNum}`);
    };
    if (isLoadingVenue) {
        return _jsx(Loading, {});
    }
    if (venueError || !venue) {
        return _jsx(ErrorView, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(ErrorSnackbar, { open: createMutation.isError, message: "Failed to add pitch. Please try again.", onClose: () => createMutation.reset() }), _jsx(Typography, { variant: "h4", gutterBottom: true, sx: { fontSize: { xs: "1.75rem", sm: "2.125rem" } }, children: "Add Pitch" }), _jsxs(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 3 }, children: ["Adding pitch to ", venue.name] }), _jsx(Box, { component: "form", onSubmit: handleSubmit(onSubmit), noValidate: true, children: _jsxs(Stack, { spacing: 3, children: [_jsx(Controller, { name: "name", control: control, rules: { required: "Pitch name is required" }, render: ({ field }) => (_jsx(TextField, { ...field, label: "Pitch Name", placeholder: "e.g., Pitch 1, Main Field", fullWidth: true, required: true, error: !!errors.name, helperText: errors.name?.message })) }), _jsx(Controller, { name: "description", control: control, render: ({ field }) => (_jsx(TextField, { ...field, label: "Description", placeholder: "e.g., Front left corner, near parking", fullWidth: true, helperText: "Optional description to help identify this pitch" })) }), _jsx(Controller, { name: "sizes", control: control, rules: {
                                validate: (value) => value.length > 0 || "At least one size is required",
                            }, render: ({ field }) => (_jsxs(FormControl, { fullWidth: true, required: true, error: !!errors.sizes, children: [_jsx(InputLabel, { id: "sizes-label", children: "Supported Sizes" }), _jsx(Select, { ...field, labelId: "sizes-label", label: "Supported Sizes", multiple: true, input: _jsx(OutlinedInput, { label: "Supported Sizes" }), renderValue: (selected) => (_jsx(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 0.5 }, children: selected.map((value) => (_jsx(Chip, { label: `${value}-a-side`, size: "small" }, value))) })), children: SIZE_OPTIONS.map((option) => (_jsx(MenuItem, { value: option.value, children: option.label }, option.value))) }), errors.sizes && (_jsx(FormHelperText, { children: errors.sizes.message }))] })) }), _jsx(Controller, { name: "surface", control: control, rules: { required: "Surface is required" }, render: ({ field }) => (_jsxs(FormControl, { fullWidth: true, required: true, error: !!errors.surface, children: [_jsx(InputLabel, { id: "surface-label", children: "Surface" }), _jsx(Select, { ...field, labelId: "surface-label", label: "Surface", children: SURFACE_OPTIONS.map((option) => (_jsx(MenuItem, { value: option.value, children: option.label }, option.value))) })] })) }), _jsx(Controller, { name: "isIndoor", control: control, render: ({ field }) => (_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: field.value, onChange: (e) => field.onChange(e.target.checked) }), label: "Indoor pitch" })) }), _jsx(FormActions, { submitLabel: "Add Pitch", onCancel: handleCancel, isSubmitting: createMutation.isLoading })] }) })] }));
};
export default CreatePitch;
